import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import "./EventConfirmList.css";

// 印刷用 HTML に値を埋め込む前に必ずエスケープし、氏名・部署名などの
// 保存データ経由の XSS（DOM-based）を防止する。
const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[ch]));

const STATUS_CONFIG = {
  participating: {
    label: "参加者リスト",
    filterFn: (p) => p.is_participating === true || p.isParticipating === true,
  },
  not_participating: {
    label: "不参加者リスト",
    filterFn: (p) => p.is_participating === false || p.isParticipating === false,
  },
  unregistered: {
    label: "未登録者リスト",
    filterFn: (p) =>
      p.is_participating === null ||
      p.is_participating === undefined,
  },
};

const EventConfirmList = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();

  const queryParams = new URLSearchParams(location.search);
  const targetDept = queryParams.get("department");
  const statusKey = queryParams.get("status") || "participating";

  const config = STATUS_CONFIG[statusKey] || STATUS_CONFIG.participating;

  const [employees, setEmployees] = useState([]);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [eventRes, participantsRes] = await Promise.all([
          api.get(`/events/${eventId}/`),
          api.get(`/events/${eventId}/results/`),
        ]);

        const filtered = participantsRes.data.filter((e) => {
          const statusMatch = config.filterFn(e);
          const deptMatch = (e.departmentName || "未所属") === targetDept;
          return statusMatch && deptMatch;
        });

        setEventInfo(eventRes.data);
        setEmployees(filtered);
      } catch (err) {
        console.error("エラーが発生しました:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId, targetDept, statusKey]);

  const formatDateJP = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const handlePrint = () => {
    const listLabel = escapeHtml(`${config.label}（${targetDept}）`);
    const eventName = escapeHtml(eventInfo?.eventName || "-");
    const eventDate = escapeHtml(formatDateJP(eventInfo?.eventDate));
    const totalCount = employees.length;

    const rows = employees.map((e) => `
      <tr>
        <td>${escapeHtml(e.memberId || "")}</td>
        <td>${escapeHtml(e.employeeName || "")}</td>
        <td>${escapeHtml(e.departmentName || "未所属")}</td>
        <td style="text-align:center;"><span class="print-checkbox"></span></td>
      </tr>
    `).join("");

    const printWindow = window.open("", "", "width=900,height=800");
    printWindow.document.write(`
      <html>
        <head>
          <title>${listLabel}</title>
          <style>
            body { font-family: "Hiragino Kaku Gothic ProN", Meiryo, sans-serif; margin: 20px 30px; }
            table { border-collapse: collapse; width: 100%; }
            thead { display: table-header-group; }
            th, td { border: 1px solid #333; padding: 8px 10px; text-align: left; font-size: 12px; }
            th { background: #dbe9ff; text-align: center; font-weight: bold; }
            .header-row td { border: none; padding: 2px 4px; background: #f8f8f8; font-size: 11px; }
            .header-title { font-size: 14px; font-weight: bold; }
            .print-checkbox { width: 16px; height: 16px; border: 1.5px solid #000; display: inline-block; }
            @page { margin: 15mm; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr class="header-row">
                <td colspan="4" class="header-title">${listLabel}</td>
              </tr>
              <tr class="header-row">
                <td colspan="2"><strong>イベント名：</strong>${eventName}</td>
                <td><strong>開催日：</strong>${eventDate}</td>
                <td><strong>合計：</strong>${totalCount}名</td>
              </tr>
              <tr>
                <th style="width:15%">コード</th>
                <th style="width:35%">氏名</th>
                <th style="width:35%">課</th>
                <th style="width:15%">確認</th>
              </tr>
            </thead>
            <tbody>
              ${rows.length > 0 ? rows : '<tr><td colspan="4" style="text-align:center;">データがありません。</td></tr>'}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) return <p className="text-center mt-4">読み込み中...</p>;

  return (
    <div className="event-confirm-list-container">
      <h2 className="form-litle">{config.label}</h2>

      {eventInfo && (
        <div className="event-info-box">
          <p><strong>イベント名：</strong> {eventInfo.eventName}</p>
          <p><strong>開催日：</strong> {formatDateJP(eventInfo.eventDate)}</p>
        </div>
      )}

      <div className="list-header-info">
        <h3>{targetDept} - 名簿合計: {employees.length} 名</h3>
      </div>

      <div ref={printRef}>
        {employees.length === 0 ? (
          <p className="text-center">データがありません。</p>
        ) : (
          <table className="table-confirm-list">
            <thead>
              <tr>
                <th style={{ width: "15%" }}>コード</th>
                <th style={{ width: "35%" }}>氏名</th>
                <th style={{ width: "35%" }}>課</th>
                <th style={{ width: "15%" }}>確認</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e, index) => (
                <tr key={index}>
                  <td>{e.memberId}</td>
                  <td>{e.employeeName}</td>
                  <td>{e.departmentName || "未所属"}</td>
                  <td>
                    <span className="print-checkbox"></span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="button-group">
        <button type="button" className="btn-print" onClick={handlePrint}>印刷</button>
        <button type="button" className="btn-back" onClick={() => navigate(`/event/department/confirm/${eventId}`)}>戻る</button>
      </div>
    </div>
  );
};

export default EventConfirmList;
